/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { FocusEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Label, LoadingSpinner, Text, TextInput } from '@blend/enterprise-components';
import { TypeaheadResults } from './TypeaheadResults';
import { debounce } from '@utils/lodash';
import { SearchFunction, SearchResults } from '@components/TypeaheadSearch/types';
import { searchAndUpdateUI } from '@components/TypeaheadSearch/search';

/** This component is an adapted version of the same component from blend-bridge repo */
const styles = StyleSheet.create({
  root: {
    position: 'relative',
  },
  spinnerContainer: {
    bottom: 3,
    position: 'absolute',
    right: '3%',
    transform: 'translateY(-50%)',
  },
});

const debounceSearch = debounce(searchAndUpdateUI, 250);

export namespace TypeaheadSearch {
  export interface Props<T> {
    id?: string;
    name: string;
    ResultComponent: React.FC<{ result: T; highlight: string }>;
    clearOnSelect?: boolean;
    inputThreshold?: number;
    isInModal?: boolean;
    label?: string;
    maxResultsHeight?: number;
    onResultClick: (result: T) => void;
    placeholder?: string;
    refocusOnSelect?: boolean;
    searchFunction: SearchFunction<T>;
    searchResultLimit?: number;
  }
}

export const TypeaheadSearch = <T,>({
  clearOnSelect = true,
  id,
  label,
  maxResultsHeight,
  inputThreshold = 2,
  name,
  onResultClick,
  placeholder = 'Search',
  refocusOnSelect,
  ResultComponent,
  searchFunction,
  searchResultLimit = 8,
}: TypeaheadSearch.Props<T>): ReactElement => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState<string>('');
  const [searchError, setSearchError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInputFocused, setInputFocused] = useState<boolean>(true);
  const [results, setResults] = useState<SearchResults<T> | undefined>();

  useEffect(() => {
    const executeSearch = async () => {
      if (!!query && query.length >= inputThreshold) {
        await debounceSearch({ query, searchFunction, setSearchError, setIsLoading, setResults });
      } else {
        setSearchError(undefined);
        setResults(undefined);
      }
    };
    executeSearch();
  }, [query, setResults, debounceSearch, searchFunction, setIsLoading]);

  const handleBlur = (e: FocusEvent) => {
    const currentTarget = e.currentTarget;
    const nextTarget = e.relatedTarget;

    if (nextTarget instanceof Element && currentTarget.contains(nextTarget)) {
      return;
    }
    setInputFocused(false);
  };

  return (
    <div
      className={css(styles.root)}
      data-it={`it-${name}-search`}
      onBlur={handleBlur}
      onFocus={() => setInputFocused(true)}
      ref={inputRef}
      tabIndex={0}
    >
      {label ? <Label labelSize="sm">{label}</Label> : null}
      <TextInput
        autoComplete="off"
        configOverrides={{
          icon: {
            position: 'left',
            size: 16,
            type: 'search',
          },
        }}
        id={id}
        onChange={e => setQuery(e.target.value)}
        placeholder={placeholder}
        type="search"
        value={query}
      />
      {isLoading && (
        <div className={css(styles.spinnerContainer)}>
          <LoadingSpinner size="md" theme="secondary" />
        </div>
      )}
      {searchError ? (
        <label htmlFor={id} data-it={'error-label'}>
          <Text colorName="ACCENT_RED_70" fontSize="sm">
            Error during search. Please try again later.
          </Text>
        </label>
      ) : null}

      {results && query.length >= inputThreshold && (
        <TypeaheadResults
          ResultComponent={ResultComponent}
          isInputFocused={isInputFocused}
          key={query}
          highlight={query.trim()}
          maxResultsHeight={maxResultsHeight}
          name={name}
          resultLimit={searchResultLimit}
          onResultClick={result => {
            onResultClick(result);
            if (clearOnSelect) {
              setQuery('');
            }
            if (refocusOnSelect && inputRef.current) {
              inputRef.current.focus();
            }
          }}
          results={results}
        />
      )}
    </div>
  );
};
